import React from 'react';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { toast } from 'react-toastify';

/**
 * One standard (cash customer) rate card: view, edit a row, delete a row, clear the
 * card, export it.
 *
 * The seven cards have seven different column shapes — zone rates, weight ranges,
 * per-region express columns, per-LGA surcharges — so they are described once in
 * COLUMNS and rendered by the same component, mirroring the backend's own registry in
 * StandardRateAdminService::CARDS. Change a card, change those two places.
 *
 * Separate from CreditRateTable on purpose: that one is per-customer and its cards
 * share nothing with these but the idea of a row.
 */

export type StandardRateType =
    | 'domestic' | 'bulk' | 'bulk_extra' | 'express' | 'export' | 'lga_domestic' | 'lga_bulk';

type Column = { key: string; label: string; money?: boolean; text?: boolean };

const COLUMNS: Record<StandardRateType, Column[]> = {
    domestic: [
        { key: 'weight_kg', label: 'Weight (KG)' },
        { key: 'zone_1_rate', label: 'Zone 1 (Lagos)', money: true },
        { key: 'zone_2_rate', label: 'Zone 2', money: true },
        { key: 'zone_3_rate', label: 'Zone 3', money: true },
        { key: 'zone_4_rate', label: 'Zone 4', money: true },
    ],
    bulk: [
        { key: 'weight_kg', label: 'Weight (KG)' },
        { key: 'zone_1_rate', label: 'Zone 1', money: true },
        { key: 'zone_2_rate', label: 'Zone 2', money: true },
        { key: 'zone_3_rate', label: 'Zone 3', money: true },
        { key: 'zone_4_rate', label: 'Zone 4', money: true },
    ],
    bulk_extra: [
        { key: 'weight_range', label: 'Range', text: true },
        { key: 'weight_from', label: 'From KG' },
        { key: 'weight_to', label: 'To KG' },
        { key: 'zone_1_rate', label: 'Zone 1', money: true },
        { key: 'zone_2_rate', label: 'Zone 2', money: true },
        { key: 'zone_3_rate', label: 'Zone 3', money: true },
        { key: 'zone_4_rate', label: 'Zone 4', money: true },
    ],
    express: [
        { key: 'weight_from', label: 'From KG' },
        { key: 'weight_to', label: 'To KG' },
        { key: 'lagos_mainland_rate', label: 'Lagos Mainland', money: true },
        { key: 'lagos_island_rate', label: 'Lagos Island', money: true },
        { key: 'south_west_rate', label: 'South West', money: true },
        { key: 'direct_rate', label: 'Direct', money: true },
        { key: 'nationwide_rate', label: 'Nationwide', money: true },
        { key: 'additional_per_kg', label: 'Add / KG', money: true },
    ],
    export: [
        { key: 'country_name', label: 'Country', text: true },
        { key: 'country_code', label: 'Code', text: true },
        { key: 'first_half_kg_rate', label: 'First 0.5KG', money: true },
        { key: 'additional_half_kg_rate', label: 'Additional 0.5KG', money: true },
    ],
    lga_domestic: [
        { key: 'lga_name', label: 'LGA', text: true },
        { key: 'state_name', label: 'State', text: true },
        { key: 'base_weight', label: 'Base Weight' },
        { key: 'upper_weight', label: 'Upper Weight' },
        { key: 'onforwarding_value', label: 'Onforwarding', money: true },
        { key: 'add_1kg_value', label: 'Add 1KG', money: true },
    ],
    lga_bulk: [
        { key: 'lga_name', label: 'LGA', text: true },
        { key: 'state_name', label: 'State', text: true },
        { key: 'base_weight', label: 'Base Weight' },
        { key: 'upper_weight', label: 'Upper Weight' },
        { key: 'onforwarding_value', label: 'Onforwarding', money: true },
        { key: 'add_1kg_value', label: 'Add 1KG', money: true },
    ],
};

const money = (v: any) =>
    `₦${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const apiError = (e: any, fallback: string) =>
    e?.response?.data?.message || e?.message || fallback;

type Props = {
    type: StandardRateType;
    rows: any[];
    /** Re-fetch this card after a change. */
    onChanged: () => void;
};

function StandardRateTable({ type, rows, onChanged }: Props) {
    const columns = COLUMNS[type];

    const [editingId, setEditingId] = React.useState<any>(null);
    const [draft, setDraft] = React.useState<any>({});
    const [busy, setBusy] = React.useState<string>('');

    // Export rates double as the country list, so removing one withdraws that country
    // from every booking form. Clearing the card is refused outright by the backend.
    const isCountryTable = type === 'export';

    const startEdit = (row: any) => {
        setEditingId(row.id);
        setDraft(Object.fromEntries(columns.map((c) => [c.key, row[c.key] ?? ''])));
    };

    const saveEdit = async (row: any) => {
        setBusy(`save-${row.id}`);
        try {
            const res: any = await SuperAdminApis.updateStandardRateRow(type, row.id, draft);
            if (res?.data?.status) {
                toast.success(res.data.message);
                setEditingId(null);
                onChanged();
            } else toast.warn(res?.data?.message || 'Could not update this rate');
        } catch (e) {
            toast.error(apiError(e, 'Could not update this rate'));
        } finally {
            setBusy('');
        }
    };

    const removeRow = async (row: any) => {
        const describes = columns.slice(0, 2).map((c) => `${c.label} ${row[c.key]}`).join(', ');

        const warning = isCountryTable
            ? `\n\nWARNING: this table is also the country list. ${row.country_name} will no longer be selectable as a destination anywhere in the system.`
            : '\n\nThis affects pricing immediately.';

        if (!window.confirm(`Delete ${describes}?${warning}`)) return;

        setBusy(`del-${row.id}`);
        try {
            const res: any = await SuperAdminApis.deleteStandardRateRow(type, row.id);
            if (res?.data?.status) { toast.success(res.data.message); onChanged(); }
            else toast.warn(res?.data?.message || 'Could not delete this rate');
        } catch (e) {
            toast.error(apiError(e, 'Could not delete this rate'));
        } finally {
            setBusy('');
        }
    };

    const deleteAll = async () => {
        const answer = window.prompt(
            `This deletes ALL ${rows.length} row(s) on this card.\n\n` +
            `Shipments in these bands cannot be priced until new rates are uploaded.\n\n` +
            `Type ${rows.length} to confirm.`
        );

        if (answer === null) return;
        if (answer.trim() !== String(rows.length)) { toast.info('Cancelled — the number did not match.'); return; }

        setBusy('delete-all');
        try {
            const res: any = await SuperAdminApis.deleteAllStandardRates(type);
            if (res?.data?.status) { toast.success(res.data.message); onChanged(); }
            else toast.warn(res?.data?.message || 'Could not clear this card');
        } catch (e) {
            toast.error(apiError(e, 'Could not clear this card'));
        } finally {
            setBusy('');
        }
    };

    const exportRates = async () => {
        setBusy('export');
        try {
            const res: any = await SuperAdminApis.exportStandardRates(type);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${type.replace('_', '-')}-rates.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiError(e, 'Could not export these rates'));
        } finally {
            setBusy('');
        }
    };

    return (
        <div>
            <div className='flex flex-wrap justify-end gap-2 mb-3'>
                <button onClick={exportRates} disabled={!rows.length || !!busy}
                    title={rows.length ? 'Download the current rates as CSV' : 'Nothing to export yet'}
                    className="bg-gray-700 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-800 disabled:opacity-40">
                    {busy === 'export' ? 'Exporting…' : 'Export Current'}
                </button>

                {/* No Delete All for export rates: the backend refuses it, because
                    emptying that table would leave no selectable countries at all. */}
                {!isCountryTable && (
                    <button onClick={deleteAll} disabled={!rows.length || !!busy}
                        className="bg-white border border-[#E04343] text-[#E04343] px-4 py-2 rounded-md text-sm hover:bg-red-50 disabled:opacity-40">
                        {busy === 'delete-all' ? 'Deleting…' : 'Delete All'}
                    </button>
                )}
            </div>

            {isCountryTable && (
                <p className='text-[11px] text-amber-700 bg-amber-50 border-l-4 border-amber-400 p-2 mb-3'>
                    These rows are also the country list used across the system. Deleting one removes
                    that country from every booking form, so the card cannot be cleared in bulk.
                </p>
            )}

            <div className="relative overflow-x-auto bg-white rounded-lg">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            {columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}
                            <th className="px-4 py-3 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? rows.map((rate: any) => {
                            const editing = editingId === rate.id;

                            return (
                                <tr key={rate.id} className="bg-white border-b hover:bg-gray-50">
                                    {columns.map((c) => (
                                        <td key={c.key} className="px-4 py-3">
                                            {editing ? (
                                                <input
                                                    type={c.text ? 'text' : 'number'}
                                                    step={c.text ? undefined : '0.01'}
                                                    min={c.text ? undefined : '0'}
                                                    value={draft[c.key] ?? ''}
                                                    onChange={(e) => setDraft({ ...draft, [c.key]: e.target.value })}
                                                    className="border rounded px-2 py-1 w-28 text-sm"
                                                />
                                            ) : c.money ? money(rate[c.key]) : (
                                                <span className="font-medium">{rate[c.key]}</span>
                                            )}
                                        </td>
                                    ))}

                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        {editing ? (
                                            <>
                                                <button onClick={() => saveEdit(rate)} disabled={!!busy}
                                                    className="text-green-700 font-medium hover:underline disabled:opacity-40">
                                                    {busy === `save-${rate.id}` ? 'Saving…' : 'Save'}
                                                </button>
                                                <button onClick={() => setEditingId(null)} className="ml-3 text-gray-500 hover:underline">Cancel</button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => startEdit(rate)} disabled={!!busy}
                                                    className="text-blue-600 font-medium hover:underline disabled:opacity-40">Edit</button>
                                                <button onClick={() => removeRow(rate)} disabled={!!busy}
                                                    className="ml-3 text-[#E04343] font-medium hover:underline disabled:opacity-40">
                                                    {busy === `del-${rate.id}` ? 'Deleting…' : 'Delete'}
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan={columns.length + 1} className="px-6 py-8 text-center text-gray-500">
                                    No rates found. Upload an Excel file to add rates.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default StandardRateTable;
